import React, { useContext, useState, useEffect } from "react";
import { Field as FieldBase, Formik as FormikBase } from "formik";

import { textMDInputFormik, textAreaMDInputFormik } from "@/helpers/formInputs";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { creativeFeedbackValidation } from "@/helpers/validations";
import { logger } from "@/helpers/loggerHelper";
import { HTTP_STATUS } from "@/config/constants";
import { Button } from "@mui/material";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";

type FeedbackValues = {
  creative_name: string;
  feedback: string;
};

type ParticipantFeedbackProps = {
  creativeId: string | number;
  creativeName?: string;
  campaign?: {
    id?: string | number;
    system_generated_campaign_id?: string;
    campaign_name?: string;
    [key: string]: unknown;
  };
  close: () => void;
  location: {
    id?: string | number;
    [key: string]: unknown;
  };
};

const Formik = FormikBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const Field = FieldBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

/**
 * Request Creative: When creatives need to be altered/modified or requesting for more creative
 */
const ParticipantFeedback = ({
  creativeId,
  creativeName,
  campaign,
  close,
  location,
}: ParticipantFeedbackProps) => {
  const { loggedInUser } = useContext(GlobalContext) as {
    loggedInUser: {
      id?: string | number;
      email?: string;
      first_name?: string;
      last_name?: string;
      participantData?: {
        id?: string | number;
      };
    };
  };
  const loader = useContext(LoaderContext) as (_isLoading: boolean) => void;

  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  const onSubmitForm = async (values: FeedbackValues) => {
    try {
      if (shouldBlockUnsafeSubmission(values)) {
        return;
      }

      setSending(true);
      const feedbackData = {
        fk_participant_id: loggedInUser.participantData?.id,
        email_id: loggedInUser.email,
        name: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
        creative_id: creativeId.toString(),
        creative_name: values.creative_name,
        remark: values.feedback,
        fk_campaign_id: `${campaign?.id}`,
        system_generated_campaign_id: campaign?.system_generated_campaign_id,
        campaign_name: campaign?.campaign_name,
        fk_location_id: location.id,
      };
      const res = await campaignService.sendCreativeFeedback(feedbackData);

      if (res.status === HTTP_STATUS.OK) {
        setSendSuccess(true);
        setTimeout(() => {
          close();
        }, 5000);
      }
    } catch (err) {
      logger.error("onSubmitForm error", err);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    loader(sending);
  }, [sending, loader]);

  return (
    <div className="participant-feedback p-2">
      <div className="card my-3">
        <div className="card-body px-1 px-sm-2">
          {sendSuccess ? (
            <h4 className="text24M_SemiBold00000b fs-14 text-center">
              Thank you for your feedback. We will contact you soon.
            </h4>
          ) : (
            <div className="row">
              <div className="col-md-4 text-center">
                <h4 className="text24M_SemiBold00000b fs-14">
                  Please share your feedback
                </h4>
                <img
                  className="mw-100 mx-auto mt-3"
                  src={`/img/feedback.png`}
                  alt=""
                />
              </div>
              <div className="col-md-8">
                <Formik
                  initialValues={{
                    creative_name: creativeName || "",
                    feedback: "",
                  }}
                  onSubmit={onSubmitForm}
                  validate={creativeFeedbackValidation}
                >
                  {({ handleSubmit, isValid, values }: any) => {
                    return (
                      <form onSubmit={handleSubmit}>
                        <Field
                          name="creative_name"
                          component={textMDInputFormik}
                          label="Creative Name"
                          shrink={true}
                          disabled={values.creative_name ? true : false}
                        />
                        <Field
                          name="feedback"
                          component={textAreaMDInputFormik}
                          label="Description/ Change Required"
                          shrink={true}
                          autoFocus
                        />
                        <Button
                          type="submit"
                          disabled={sending || !isValid}
                          className="float-right"
                          color="primary"
                          variant="contained"
                          size="small"
                        >
                          Submit
                        </Button>
                      </form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantFeedback;
